import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiPlus, FiGrid, FiList, FiChevronLeft, FiChevronRight, FiSearch, FiFilter } from 'react-icons/fi';
import ProblemCard from '../components/ProblemCard';
import ProblemFilters from '../components/ProblemFilters';
import RevisionScheduleModal from '../components/RevisionScheduleModal';

const Problems = () => {
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    platform: '',
    difficulty: '',
    category: '',
    pattern: '',
    favorites: false,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0,
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [revisionModalOpen, setRevisionModalOpen] = useState(false);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchProblems();
  }, [filters, pagination.current, sortBy, sortOrder]);

  const fetchProblems = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: 12,
        sortBy,
        sortOrder,
        ...filters,
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await axios.get('/api/problems', { params });
      setProblems(response.data.problems);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching problems:', error);
      toast.error('Failed to fetch problems');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleSearchChange = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      platform: '',
      difficulty: '',
      category: '',
      pattern: '',
      favorites: false,
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleToggleFavorite = async (problemId) => {
    try {
      const response = await axios.put(`/api/problems/${problemId}/favorite`);
      setProblems(prev =>
        prev.map(problem =>
          problem._id === problemId
            ? { ...problem, isFavorite: response.data.problem.isFavorite }
            : problem
        )
      );
      toast.success(
        response.data.problem.isFavorite
          ? 'Added to favorites'
          : 'Removed from favorites'
      );
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorite status');
    }
  };

  const handleScheduleRevision = (problemId) => {
    const problem = problems.find(p => p._id === problemId);
    setSelectedProblem(problem);
    setRevisionModalOpen(true);
  };

  const handleRevisionScheduled = () => {
    // Refresh the problems list to show updated revision schedule
    fetchProblems();
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
  };

  // Calculate active filters count
  const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'search') return value.trim() !== '';
    if (typeof value === 'boolean') return value;
    return value !== '';
  }).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-purple-900/20">
      <div className="container mx-auto px-4 py-8 space-y-8">
        {/* Enhanced Header */}
        <div className="text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-4">
            Problem Collection
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Master your coding skills with our comprehensive problem tracking system
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
              <div className="w-2 h-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"></div>
              <span>{pagination.total} Total Problems</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
              <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-green-600 rounded-full"></div>
              <span>{problems.filter(p => p.status === 'solved').length} Solved</span>
            </div>
          </div>
        </div>

        {/* Enhanced Action Bar */}
        <div className="card">
          <div className="card-body">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Search and Filter Controls */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
                <div className="relative flex-1 max-w-md">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search problems..."
                    value={filters.search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
                
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`relative inline-flex items-center px-4 py-2 rounded-xl border transition-all duration-200 ${
                    showFilters || activeFiltersCount > 0
                      ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-700 text-primary-700 dark:text-primary-300'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <FiFilter className="w-4 h-4 mr-2" />
                  Filters
                  {activeFiltersCount > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-primary-600 text-white text-xs rounded-full">
                      {activeFiltersCount}
                    </span>
                  )}
                </button>
              </div>

              {/* View Mode and Actions */}
              <div className="flex items-center justify-between sm:justify-end gap-3">
                <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-all duration-200 ${
                      viewMode === 'grid'
                        ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <FiGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-all duration-200 ${
                      viewMode === 'list'
                        ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <FiList className="w-4 h-4" />
                  </button>
                </div>
                
                <Link
                  to="/problems/add"
                  className="btn-primary inline-flex items-center px-6 py-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <FiPlus className="w-4 h-4 mr-2" />
                  Add Problem
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Filters */}
        {showFilters && (
          <div className="card animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="card-body">
              <ProblemFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onSearchChange={handleSearchChange}
                onClearFilters={handleClearFilters}
                totalCount={pagination.total}
              />
            </div>
          </div>
        )}

        {/* Enhanced Sort Options */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort by:</span>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [newSortBy, newSortOrder] = e.target.value.split('-');
                setSortBy(newSortBy);
                setSortOrder(newSortOrder);
              }}
              className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
            >
              <option value="createdAt-desc">Newest First</option>
              <option value="createdAt-asc">Oldest First</option>
              <option value="solveDate-desc">Recently Solved</option>
              <option value="solveDate-asc">Oldest Solved</option>
              <option value="timeTaken-asc">Quickest First</option>
              <option value="timeTaken-desc">Longest First</option>
              <option value="problemName-asc">Name A-Z</option>
              <option value="problemName-desc">Name Z-A</option>
            </select>
          </div>
          
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing {problems.length} of {pagination.total} problems
          </div>
        </div>

        {/* Enhanced Problems Grid/List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 dark:border-gray-700"></div>
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-600 border-t-transparent absolute top-0 left-0"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-6 bg-primary-600 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>
        ) : problems.length > 0 ? (
          <>
            <div className={`animate-in fade-in slide-in-from-bottom-4 duration-500 ${
              viewMode === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-4'
            }`}>
              {problems.map((problem, index) => (
                <div 
                  key={problem._id}
                  className="animate-in fade-in slide-in-from-bottom-4"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <ProblemCard
                    problem={problem}
                    onToggleFavorite={handleToggleFavorite}
                    onScheduleRevision={handleScheduleRevision}
                  />
                </div>
              ))}
            </div>

            {/* Enhanced Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-center">
                <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 rounded-2xl p-2 shadow-lg border border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => handlePageChange(pagination.current - 1)}
                    disabled={pagination.current === 1}
                    className="p-3 rounded-xl text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <FiChevronLeft className="w-5 h-5" />
                  </button>

                  <div className="flex space-x-1">
                    {Array.from({ length: Math.min(pagination.pages, 7) }, (_, i) => {
                      let page;
                      if (pagination.pages <= 7) {
                        page = i + 1;
                      } else if (pagination.current <= 4) {
                        page = i + 1;
                      } else if (pagination.current >= pagination.pages - 3) {
                        page = pagination.pages - 6 + i;
                      } else {
                        page = pagination.current - 3 + i;
                      }
                      
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                            page === pagination.current
                              ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg transform scale-105'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(pagination.current + 1)}
                    disabled={pagination.current === pagination.pages}
                    className="p-3 rounded-xl text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <FiChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <div className="relative mx-auto mb-8">
              <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-3xl flex items-center justify-center shadow-xl">
                <FiPlus className="w-16 h-16 text-gray-400 dark:text-gray-500" />
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-lg">
                <FiSearch className="w-4 h-4 text-white" />
              </div>
            </div>
            
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {Object.values(filters).some(f => f !== '' && f !== false)
                ? 'No problems match your filters'
                : 'Start Your Coding Journey'}
            </h3>
            
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
              {Object.values(filters).some(f => f !== '' && f !== false)
                ? 'Try adjusting your search criteria or clear the filters to see all problems.'
                : 'Add your first coding problem and begin tracking your progress toward mastery!'}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {Object.values(filters).some(f => f !== '' && f !== false) && (
                <button
                  onClick={handleClearFilters}
                  className="px-6 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
                >
                  Clear Filters
                </button>
              )}
              
              <Link
                to="/problems/add"
                className="px-8 py-3 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 inline-flex items-center"
              >
                <FiPlus className="w-5 h-5 mr-2" />
                Add Your First Problem
              </Link>
            </div>
          </div>
        )}

        {/* Revision Schedule Modal */}
        <RevisionScheduleModal
          isOpen={revisionModalOpen}
          onClose={() => setRevisionModalOpen(false)}
          problem={selectedProblem}
          onSuccess={handleRevisionScheduled}
        />
      </div>
    </div>
  );
};

export default Problems;